<template>
  <div class="ip-page py-3">
    <header class="ip-toolbar">
      <div class="ip-toolbar__title">
        <span class="ip-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Incentive Preview</h1>
          <p>Daily attribution of HMS patients to tele-sales agents — locked at month-end</p>
        </div>
      </div>
      <div class="ip-toolbar__actions">
        <CButton size="sm" class="ip-credit-all-btn" :disabled="isCreditingAll || !summary.eligible" @click="creditAll">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 5v14M5 12h14" />
          </svg>
          <span>{{ isCreditingAll ? 'Crediting…' : `Credit All Eligible (${summary.eligible || 0})` }}</span>
        </CButton>
        <CButton size="sm" class="ip-export-btn" :disabled="isExporting || !pagination.total" @click="exportXlsx">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          <span>{{ isExporting ? 'Exporting…' : 'Export XLSX' }}</span>
        </CButton>
        <CButton size="sm" class="ip-recompute-btn" :disabled="isRecomputing" @click="recompute">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="{ 'is-spinning': isRecomputing }" aria-hidden="true">
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
          </svg>
          <span>{{ isRecomputing ? 'Recomputing…' : 'Recompute' }}</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="ip-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="ip-alert">{{ successMessage }}</CAlert>

    <section class="ip-tiles">
      <article class="ip-tile ip-tile--total">
        <div class="ip-tile__label">Total Patients</div>
        <div class="ip-tile__value">{{ summary.total }}</div>
      </article>
      <article class="ip-tile ip-tile--eligible">
        <div class="ip-tile__label">Eligible</div>
        <div class="ip-tile__value">{{ summary.eligible }}</div>
        <div class="ip-tile__breakdown">
          <span>Mobile matches — auto-credited</span>
        </div>
      </article>
      <article class="ip-tile ip-tile--ineligible">
        <div class="ip-tile__label">Ineligible</div>
        <div class="ip-tile__value">{{ ineligibleCount }}</div>
        <div class="ip-tile__breakdown">
          <span>{{ summary.ineligible_no_call }} no call</span>
          <span>{{ summary.ineligible_oms }} OMS customer</span>
        </div>
      </article>
      <article class="ip-tile ip-tile--review">
        <div class="ip-tile__label">Needs Review</div>
        <div class="ip-tile__value">{{ needsReviewCount }}</div>
        <div class="ip-tile__breakdown">
          <span>{{ summary.needs_review }} name {{ summary.needs_review === 1 ? 'match' : 'matches' }}</span>
          <span v-if="summary.needs_review_oms">{{ summary.needs_review_oms }} OMS shared phone</span>
        </div>
      </article>
      <article class="ip-tile ip-tile--payout">
        <div class="ip-tile__label">Pending Payout</div>
        <div class="ip-tile__value">₹{{ formatAmount(summary.payout_pending) }}</div>
        <div v-if="summary.payout_locked" class="ip-tile__breakdown">
          <span>Locked: ₹{{ formatAmount(summary.payout_locked) }}</span>
        </div>
      </article>
    </section>

    <section class="ip-section">
      <header class="ip-section__head">
        <h2>Filters</h2>
      </header>
      <div class="ip-section__body">
        <div class="ip-filters">
          <div class="ip-field ip-field--full">
            <label class="ip-label">Search</label>
            <AppInput
              no-margin
              :value="filters.search"
              @update:value="onSearchInput"
              placeholder="Search by OP no., patient name, mobile, or agent…"
            />
          </div>
          <div class="ip-field">
            <label class="ip-label">From Reg Date</label>
            <SingleDatePicker :value="filters.from" @input="filters.from = $event" />
          </div>
          <div class="ip-field">
            <label class="ip-label">To Reg Date</label>
            <SingleDatePicker :value="filters.to" @input="filters.to = $event" />
          </div>
          <div class="ip-field">
            <label class="ip-label">Agent</label>
            <AppDropdown
              v-model="filters.agent_id"
              :options="agentOptions"
              searchable
              search-placeholder="Search agent…"
              placeholder="Any Agent"
            />
          </div>
          <div class="ip-field">
            <label class="ip-label">Status</label>
            <AppDropdown
              v-model="filters.status"
              :options="statusOptions"
              placeholder="Any Status"
            />
          </div>
          <div class="ip-field ip-field--action">
            <label class="ip-label" aria-hidden="true">&nbsp;</label>
            <button type="button" class="ip-apply-btn" :disabled="isLoading" @click="applyFilters">{{ isLoading ? 'Loading…' : 'Apply' }}</button>
          </div>
          <div class="ip-field ip-field--action">
            <label class="ip-label" aria-hidden="true">&nbsp;</label>
            <button type="button" class="ip-reset-btn" @click="resetFilters">Reset</button>
          </div>
        </div>
      </div>
    </section>

    <BaseTable
      title="Attributions"
      :items="tableRows"
      :columns="columns"
      :loading="isLoading"
      :sortable="false"
      :show-count="false"
      max-body-height="65vh"
      empty-text="No attributions match these filters."
    >
      <template #toolbar>
        <span v-if="pagination.total" class="ip-count">{{ rangeLabel }}</span>
      </template>
      <template #cell-mobile="{ row }">
        <div class="ip-mobile-cell">
          <span class="ip-mobile-primary">{{ row._hms_mobile || '—' }}</span>
          <span v-if="row._match_type === 'name' && row._oms_phone && row._oms_phone !== row._hms_mobile" class="ip-mobile-oms" title="Phone the agent actually called (different from the patient's HMS-registered mobile)">
            OMS: {{ row._oms_phone }}
          </span>
        </div>
      </template>
      <template #cell-agent="{ row }">
        <div class="ip-agent-cell">
          <span class="ip-agent-name">{{ row.agent }}</span>
          <span v-if="row._match_type === 'name'" class="ip-match-pill ip-match-pill--name" title="Matched by name + place (mobile didn't match) — lower confidence than a mobile match">Name match</span>
          <span v-else-if="row._match_type === 'ambiguous'" class="ip-match-pill ip-match-pill--ambiguous" title="Multiple agents called this name+place within 60 days — admin must verify">Ambiguous</span>
        </div>
      </template>
      <template #cell-status="{ row }">
        <span class="ip-pill" :class="statusPillClass(row._raw_status)">{{ statusLabel(row._raw_status) }}</span>
      </template>
      <template #cell-reason="{ value }">
        <span class="ip-reason">{{ value || '—' }}</span>
      </template>
      <template #cell-incentive="{ value }">
        <span :class="value > 0 ? 'ip-amount ip-amount--paid' : 'ip-amount'">{{ value > 0 ? `₹${formatAmount(value)}` : '—' }}</span>
      </template>
      <template #cell-action="{ row }">
        <div v-if="row._raw_status === 'eligible'" class="ip-row-actions">
          <button
            type="button"
            class="ip-edit-btn"
            :disabled="creditingIds[row._id]"
            title="Credit a different agent — the mobile match picked the wrong one"
            @click="openEdit(row)"
          >
            Edit
          </button>
          <button
            type="button"
            class="ip-credit-btn"
            :disabled="creditingIds[row._id]"
            @click="creditOne(row._id)"
          >
            {{ creditingIds[row._id] ? '…' : '+ Credit' }}
          </button>
        </div>
        <span v-else-if="row._raw_status === 'locked'" class="ip-credited">✓ Credited</span>
        <button
          v-else-if="row._raw_status === 'needs_review_oms'"
          type="button"
          class="ip-credit-btn ip-credit-btn--oms"
          @click="openOmsReview(row)"
          title="Phone has an OMS order but is shared by multiple registrants — review before crediting"
        >
          Review
        </button>
        <button
          v-else
          type="button"
          class="ip-credit-btn"
          :class="reviewVariantClass(row)"
          :title="reviewTitle(row)"
          @click="openReview(row)"
        >
          Review
        </button>
      </template>
    </BaseTable>

    <CModal
      :show.sync="reviewOpen"
      size="lg"
      :centered="true"
      :title="reviewModalTitle"
      class="ip-review-modal"
      :close-on-backdrop="!isApprovingReview"
    >
      <div v-if="reviewRow" class="ip-review-body">
        <div v-if="reviewError" class="ip-review-alert">{{ reviewError }}</div>

        <div class="ip-review-patient">
          <div class="ip-review-patient__head">
            <span class="ip-review-eyebrow">HMS Patient</span>
            <h3>{{ reviewPatient.patient_name || '—' }}</h3>
          </div>
          <div class="ip-review-patient__grid">
            <div><span class="ip-rk">OP No.</span><span class="ip-rv">{{ reviewPatient.op_no || '—' }}</span></div>
            <div><span class="ip-rk">Reg Date</span><span class="ip-rv">{{ formatDate(reviewPatient.reg_date) }}</span></div>
            <div><span class="ip-rk">Mobile</span><span class="ip-rv">{{ reviewPatient.mobile || '—' }}</span></div>
            <!-- Age/gender/place come from the /candidates payload, which
                 edit mode doesn't call — the preview list doesn't carry them,
                 and they aren't needed to decide who gets the credit. -->
            <div v-if="!isEditMode"><span class="ip-rk">Age / Gender</span><span class="ip-rv">{{ reviewPatient.age || '—' }} / {{ reviewPatient.gender || '—' }}</span></div>
            <div v-if="!isEditMode" class="ip-review-patient__place">
              <span class="ip-rk">Place</span>
              <span class="ip-rv">{{ reviewPatient.place || '—' }}<span v-if="reviewPatient.house_name"> &middot; {{ reviewPatient.house_name }}</span></span>
            </div>
            <div v-if="reviewPlaceTokens.length" class="ip-review-patient__place">
              <span class="ip-rk">Place tokens</span>
              <span class="ip-rv">
                <span v-for="t in reviewPlaceTokens" :key="t" class="ip-token">{{ t }}</span>
              </span>
            </div>
          </div>
        </div>

        <!-- Edit mode (eligible rows): the engine already made a confident
             mobile match, so candidate name+place calls would be noise.
             Show who it currently credits, then let the admin swap. -->
        <div v-if="isEditMode" class="ip-current-agent">
          <span class="ip-review-eyebrow">Currently credits</span>
          <div class="ip-current-agent__row">
            <span class="ip-current-agent__name">{{ reviewRow && reviewRow.agent ? reviewRow.agent : '—' }}</span>
            <span class="ip-match-pill ip-match-pill--mobile">Mobile match</span>
          </div>
          <p class="ip-current-agent__hint">Matched automatically on the patient's mobile number. Change the agent below only if that match is wrong — the credit is recorded as a manual assignment.</p>
        </div>

        <template v-if="!isEditMode">
        <div class="ip-review-head">
          <h4>Candidate Agents <span v-if="!isLoadingCandidates">({{ reviewCandidates.length }})</span></h4>
          <p v-if="reviewCandidates.length && reviewWindowDays">All calls placed within {{ reviewWindowDays }} days before registration where the name normalised the same and at least one place token overlaps. Pick the agent who genuinely brought this patient in.</p>
        </div>

        <div v-if="isLoadingCandidates" class="ip-review-empty">Loading candidates…</div>
        <div v-else-if="!reviewCandidates.length" class="ip-review-empty">No auto-matched candidate calls for this patient — assign the agent manually below.</div>
        <div v-else class="ip-review-cards">
          <label
            v-for="cand in reviewCandidates"
            :key="cand.call_id"
            class="ip-cand"
            :class="{ 'is-picked': pickedCallId === cand.call_id }"
          >
            <input
              type="radio"
              :value="cand.call_id"
              :checked="pickedCallId === cand.call_id"
              class="ip-cand__radio"
              @change="pickCandidate(cand)"
            />
            <div class="ip-cand__body">
              <div class="ip-cand__head">
                <span class="ip-cand__agent">{{ cand.agent_name }}</span>
                <span class="ip-cand__when">{{ callDateTimeDisplay(cand.call_at) }}</span>
              </div>
              <div class="ip-cand__row">
                <span class="ip-rk">Contact</span>
                <span class="ip-rv">{{ cand.contact_name || '—' }}<span v-if="cand.contact_phone"> &middot; {{ cand.contact_phone }}</span></span>
              </div>
              <div class="ip-cand__row">
                <span class="ip-rk">Call</span>
                <span class="ip-rv">
                  <span class="ip-cand__status">{{ cand.call_status || '—' }}</span>
                  <span v-if="cand.call_type"> &middot; {{ cand.call_type }}</span>
                  <span v-if="cand.duration"> &middot; {{ cand.duration }}s</span>
                </span>
              </div>
              <div v-if="cand.addresses && cand.addresses.length" class="ip-cand__row">
                <span class="ip-rk">Address</span>
                <span class="ip-rv">
                  <span v-for="(a, i) in cand.addresses" :key="i" class="ip-cand__addr">{{ a }}</span>
                </span>
              </div>
              <div v-if="cand.matched_tokens && cand.matched_tokens.length" class="ip-cand__row">
                <span class="ip-rk">Matched tokens</span>
                <span class="ip-rv">
                  <span v-for="t in cand.matched_tokens" :key="t" class="ip-token ip-token--match">{{ t }}</span>
                </span>
              </div>
              <div v-if="cand.comments" class="ip-cand__row">
                <span class="ip-rk">Notes</span>
                <span class="ip-rv ip-cand__notes">{{ cand.comments }}</span>
              </div>
            </div>
          </label>
        </div>
        </template>

        <div class="ip-review-head">
          <h4>{{ isEditMode ? 'Credit A Different Agent' : 'Assign To Agent Manually' }}</h4>
          <p v-if="isEditMode">Pick the agent who actually brought this patient in. Leaving it unchanged credits the same agent the mobile match already picked.</p>
          <p v-else>No matching call, or a tele-sales agent raised that this patient is genuinely theirs? Pick the agent to credit. This records an admin override and credits ₹50.</p>
        </div>
        <div class="ip-manual-assign">
          <AppDropdown
            v-model="manualAgentId"
            :options="manualAgentOptions"
            searchable
            search-placeholder="Search agent…"
            placeholder="Select agent to credit"
          />
          <textarea
            v-model="reviewNote"
            class="ip-manual-note"
            rows="2"
            placeholder="Note (optional) — why this patient is being credited to the agent"
          ></textarea>
        </div>
      </div>
      <template #footer>
        <button type="button" class="ip-modal-btn ip-modal-btn--ghost" :disabled="isApprovingReview" @click="closeReview">Cancel</button>
        <button
          type="button"
          class="ip-modal-btn ip-modal-btn--primary"
          :disabled="isApprovingReview || (!pickedAgentId && !manualAgentId)"
          @click="approveReview"
        >
          {{ isApprovingReview ? 'Approving…' : 'Approve & Credit ₹50' }}
        </button>
      </template>
    </CModal>

    <CModal
      :show.sync="omsReviewOpen"
      size="lg"
      :centered="true"
      title="Review OMS Shared Phone"
      class="ip-review-modal"
      :close-on-backdrop="!isApprovingOms"
    >
      <div v-if="omsReviewRow" class="ip-review-body">
        <div v-if="omsReviewError" class="ip-review-alert">{{ omsReviewError }}</div>

        <p class="ip-oms-note">
          This patient's mobile already has an OMS order, but the number is shared by more than one
          registrant — so the order may belong to a different person in the household. Confirm this is a
          genuinely new patient before crediting the agent.
        </p>

        <div class="ip-review-patient">
          <div class="ip-review-patient__head">
            <span class="ip-review-eyebrow">HMS Patient</span>
            <h3>{{ omsReviewPatient.patient_name || '—' }}</h3>
          </div>
          <div class="ip-review-patient__grid">
            <div><span class="ip-rk">OP No.</span><span class="ip-rv">{{ omsReviewPatient.op_no || '—' }}</span></div>
            <div><span class="ip-rk">Reg Date</span><span class="ip-rv">{{ formatDate(omsReviewPatient.reg_date) }}</span></div>
            <div><span class="ip-rk">Mobile</span><span class="ip-rv">{{ omsReviewPatient.mobile || '—' }}</span></div>
            <div><span class="ip-rk">Age / Gender</span><span class="ip-rv">{{ omsReviewPatient.age || '—' }} / {{ omsReviewPatient.gender || '—' }}</span></div>
            <div class="ip-review-patient__place">
              <span class="ip-rk">Place</span>
              <span class="ip-rv">{{ omsReviewPatient.place || '—' }}<span v-if="omsReviewPatient.house_name"> &middot; {{ omsReviewPatient.house_name }}</span></span>
            </div>
          </div>
        </div>

        <div v-if="isLoadingOmsReview" class="ip-review-empty">Loading details…</div>
        <template v-else>
          <div class="ip-review-head">
            <h4>Agent To Credit</h4>
          </div>
          <div class="ip-oms-agent">
            <span class="ip-oms-agent__name">{{ omsReviewAgent && omsReviewAgent.name ? omsReviewAgent.name : '—' }}</span>
            <span v-if="omsReviewCall" class="ip-oms-agent__call">
              Last call {{ callDateTimeDisplay(omsReviewCall.created_at) }}
              <span v-if="omsReviewCall.status"> &middot; {{ omsReviewCall.status }}</span>
            </span>
            <span class="ip-match-pill ip-match-pill--name" v-if="omsReviewMatchType === 'name'">Name match</span>
            <span class="ip-match-pill ip-match-pill--mobile" v-else>Mobile match</span>
          </div>

          <div class="ip-review-head">
            <h4>OMS Order{{ omsOrders.length === 1 ? '' : 's' }} On This Number ({{ omsOrders.length }})</h4>
            <p>Check whether the order was placed under a different name — that's the signal this patient is a separate customer.</p>
          </div>
          <div v-if="!omsOrders.length" class="ip-review-empty">No OMS order rows found.</div>
          <div v-else class="ip-oms-list">
            <div v-for="o in omsOrders" :key="o.id" class="ip-oms-item">
              <div class="ip-oms-item__head">
                <span class="ip-oms-item__title">{{ o.order_number || ('Order #' + o.id) }}</span>
                <span class="ip-oms-item__amount">₹{{ formatAmount(o.grand_total) }}</span>
              </div>
              <div class="ip-oms-item__row">
                <span class="ip-rk">Customer</span>
                <span class="ip-rv" :class="{ 'ip-oms-diff': isDifferentPerson(o.contact_name) }">
                  {{ o.contact_name || '—' }}<span v-if="o.contact_phone"> &middot; {{ o.contact_phone }}</span>
                  <span v-if="isDifferentPerson(o.contact_name)" class="ip-oms-tag">different name</span>
                </span>
              </div>
              <div class="ip-oms-item__row">
                <span class="ip-rk">Order Date</span>
                <span class="ip-rv">{{ orderDateDisplay(o) }}</span>
              </div>
            </div>
          </div>

          <div class="ip-review-head">
            <h4>Others Registered On This Number ({{ omsRegistrants.length }})</h4>
          </div>
          <div v-if="!omsRegistrants.length" class="ip-review-empty">No other registrants found.</div>
          <div v-else class="ip-oms-list">
            <div v-for="r in omsRegistrants" :key="r.id" class="ip-oms-item">
              <div class="ip-oms-item__head">
                <span class="ip-oms-item__title">{{ r.patient_name || '—' }}</span>
                <span class="ip-oms-item__when">OP {{ r.op_no || '—' }} &middot; {{ formatDate(r.reg_date) }}</span>
              </div>
              <div v-if="r.place || r.house_name" class="ip-oms-item__row">
                <span class="ip-rk">Place</span>
                <span class="ip-rv">{{ r.place || '—' }}<span v-if="r.house_name"> &middot; {{ r.house_name }}</span></span>
              </div>
            </div>
          </div>
        </template>
      </div>
      <template #footer>
        <button type="button" class="ip-modal-btn ip-modal-btn--ghost" :disabled="isApprovingOms" @click="closeOmsReview">Cancel</button>
        <button
          type="button"
          class="ip-modal-btn ip-modal-btn--primary"
          :disabled="isApprovingOms || isLoadingOmsReview || !omsReviewAgent"
          @click="approveOmsReview"
        >
          {{ isApprovingOms ? 'Crediting…' : 'Approve & Credit ₹50' }}
        </button>
      </template>
    </CModal>

    <div v-if="!isLoading && tableRows.length && pagination.last_page > 1" class="ip-pagination-bar">
      <span class="ip-pagination-info">{{ rangeLabel }}</span>
      <AppPager
        :current-page="Number(pagination.current_page) || 1"
        :last-page="Number(pagination.last_page) || 1"
        @change="goToPage"
      />
    </div>

    <AppConfirmDialog
      :visible="confirmCreditAllOpen"
      title="Credit All Eligible Incentives?"
      :message="creditAllMessage"
      confirm-label="Credit All"
      busy-label="Crediting…"
      cancel-label="Cancel"
      :busy="isCreditingAll"
      variant="warning"
      @confirm="confirmCreditAll"
      @cancel="cancelCreditAll"
      @update:visible="(v) => !v && cancelCreditAll()"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppInput from '@/components/AppInput.vue'
import AppDropdown from '@/components/AppDropdown.vue'
import SingleDatePicker from '@/components/SingleDatePicker.vue'
import BaseTable from '@/components/BaseTable.vue'
import AppPager from '@/components/AppPager.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function startOfMonthIso () {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

function todayIso () {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function formatDate (value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`
}

function formatDateTime (value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${formatDate(value)}, ${hh}:${mm}`
}

const DEFAULT_SUMMARY = {
  total: 0,
  eligible: 0,
  ineligible_no_call: 0,
  ineligible_oms: 0,
  ineligible_returning: 0,
  needs_review: 0,
  needs_review_oms: 0,
  locked: 0,
  payout_pending: 0,
  payout_locked: 0
}

export default {
  name: 'IncentivePreview',
  components: { AppInput, AppDropdown, SingleDatePicker, BaseTable, AppPager, AppConfirmDialog },
  data () {
    return {
      filters: {
        from: startOfMonthIso(),
        to: todayIso(),
        agent_id: '',
        status: '',
        search: ''
      },
      searchDebounce: null,
      agents: [],
      attributions: [],
      summary: { ...DEFAULT_SUMMARY },
      pagination: { current_page: 1, per_page: 25, total: 0, last_page: 1 },
      isLoading: false,
      isRecomputing: false,
      isExporting: false,
      isCreditingAll: false,
      creditingIds: {},
      confirmCreditAllOpen: false,
      errorMessage: '',
      successMessage: '',
      reviewOpen: false,
      // 'review' = engine wasn't confident (needs_review / ineligible_*).
      // 'edit'   = eligible row whose mobile match the admin wants to override.
      reviewMode: 'review',
      reviewRow: null,
      reviewPatient: {},
      reviewCandidates: [],
      reviewPlaceTokens: [],
      reviewWindowDays: 60,
      reviewError: '',
      isLoadingCandidates: false,
      isApprovingReview: false,
      pickedCallId: null,
      pickedAgentId: null,
      manualAgentId: '',
      reviewNote: '',
      omsReviewOpen: false,
      omsReviewRow: null,
      omsReviewPatient: {},
      omsReviewAgent: null,
      omsReviewCall: null,
      omsReviewMatchType: '',
      omsRegistrants: [],
      omsOrders: [],
      omsReviewError: '',
      isLoadingOmsReview: false,
      isApprovingOms: false
    }
  },
  computed: {
    columns () {
      return [
        { key: 'sno',           label: 'SNo',           width: '70px' },
        { key: 'op_no',         label: 'OP No.',        width: '100px', sticky: true },
        { key: 'patient_name',  label: 'Patient',       width: '200px', sticky: true },
        { key: 'mobile',        label: 'Mobile',        width: '130px' },
        { key: 'reg_date',      label: 'Reg Date',      width: '120px' },
        { key: 'agent',         label: 'Agent',         width: '160px' },
        { key: 'last_call_at',  label: 'Last Call',     width: '170px' },
        { key: 'status',        label: 'Status',        width: '120px' },
        { key: 'reason',        label: 'Reason' },
        { key: 'incentive',     label: 'Incentive',     width: '100px', align: 'right' },
        { key: 'action',        label: 'Action',        width: '150px', align: 'right', stickyRight: true }
      ]
    },
    isEditMode () {
      return this.reviewMode === 'edit'
    },
    reviewModalTitle () {
      return this.isEditMode ? 'Change Credited Agent' : 'Review & Assign Attribution'
    },
    tableRows () {
      const page = Number(this.pagination.current_page) || 1
      const perPage = Number(this.pagination.per_page) || 25
      return this.attributions.map((row, index) => {
        const hmsMobile = row.patient && row.patient.mobile ? row.patient.mobile : ''
        const omsPhone = row.call && row.call.contact && row.call.contact.phone ? row.call.contact.phone : ''
        return {
          sno: (page - 1) * perPage + index + 1,
          op_no: row.patient && row.patient.op_no ? row.patient.op_no : '—',
          patient_name: row.patient && row.patient.patient_name ? row.patient.patient_name : '—',
          mobile: hmsMobile || '—',
          _hms_mobile: hmsMobile,
          _oms_phone: omsPhone,
          reg_date: row.patient && row.patient.reg_date ? formatDate(row.patient.reg_date) : '—',
          agent: row.agent && row.agent.name ? row.agent.name : '—',
          last_call_at: formatDateTime(row.last_call_at),
          _raw_status: row.status,
          _match_type: row.match_type || '',
          _id: row.id,
          _agent_user_id: row.agent_user_id || null,
          reason: row.ineligible_reason,
          incentive: Number(row.incentive_amount) || 0
        }
      })
    },
    agentOptions () {
      return [{ value: '', label: 'Any Agent' }].concat(
        this.agents.map((u) => ({ value: String(u.id), label: u.name }))
      )
    },
    manualAgentOptions () {
      // No "Any Agent" entry — the review modal needs a concrete pick.
      return this.agents.map((u) => ({ value: String(u.id), label: u.name }))
    },
    statusOptions () {
      // Name matches never reach Eligible — they sit in Needs
      // Review until admin clicks "+ Credit" on the row to approve.
      // Eligible is therefore mobile-only.
      return [
        { value: '',                       label: 'Any Status' },
        { value: 'eligible',               label: 'Eligible (Mobile)' },
        { value: 'needs_review',           label: 'Needs Review (Name)' },
        { value: 'needs_review_oms',       label: 'OMS Shared (Review)' },
        { value: 'ineligible_no_call',     label: 'No Call' },
        { value: 'ineligible_oms',         label: 'OMS Customer' },
        { value: 'locked',                 label: 'Locked' }
      ]
    },
    ineligibleCount () {
      return (Number(this.summary.ineligible_no_call) || 0)
        + (Number(this.summary.ineligible_oms) || 0)
    },
    needsReviewCount () {
      return (Number(this.summary.needs_review) || 0)
        + (Number(this.summary.needs_review_oms) || 0)
    },
    creditAllMessage () {
      const count = Number(this.summary.eligible) || 0
      const amount = Number(this.summary.payout_pending) || 0
      const formatted = amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      return `₹${formatted} will be credited across ${count} eligible attribution${count === 1 ? '' : 's'}. The attributions will be locked and can't be re-credited from the UI.`
    },
    rangeLabel () {
      const total = Number(this.pagination.total) || 0
      if (!total) return '0 patients'
      const page = Number(this.pagination.current_page) || 1
      const perPage = Number(this.pagination.per_page) || 25
      const start = (page - 1) * perPage + 1
      const end = Math.min(page * perPage, total)
      return `${start}–${end} of ${total}`
    }
  },
  watch: {
    manualAgentId (value) {
      // Choosing an agent manually clears any candidate-call selection so
      // the two assignment paths never both submit.
      if (value) {
        this.pickedCallId = null
        this.pickedAgentId = null
      }
    }
  },
  created () {
    this.loadAgents()
    this.refresh()
  },
  methods: {
    authToken () {
      return localStorage.getItem('api_token')
    },
    parseError (error, fallback) {
      const data = error && error.response && error.response.data
      if (data) {
        if (typeof data === 'string') return data
        if (data.message) return data.message
        if (data.errors) {
          const k = Object.keys(data.errors)[0]
          if (k && Array.isArray(data.errors[k])) return data.errors[k][0]
        }
      }
      return fallback
    },
    queryParams () {
      const p = { token: this.authToken() }
      if (this.filters.from) p.from = this.filters.from
      if (this.filters.to) p.to = this.filters.to
      if (this.filters.agent_id) p.agent_id = this.filters.agent_id
      if (this.filters.status) p.status = this.filters.status
      if (this.filters.search && this.filters.search.trim()) p.search = this.filters.search.trim()
      return p
    },
    loadAgents () {
      axios.get(`${this.$apiAdress}/api/users?token=${this.authToken()}`)
        .then((response) => {
          const list = Array.isArray(response.data)
            ? response.data
            : Array.isArray(response && response.data && response.data.users) ? response.data.users : []
          this.agents = list.map((u) => ({ id: u.id, name: u.name || `User ${u.id}` }))
        })
        .catch(() => {
          // Non-fatal — the filter just won't be populated.
        })
    },
    refresh () {
      this.errorMessage = ''
      this.successMessage = ''
      this.loadSummary()
      this.loadPreview(1)
    },
    loadSummary () {
      axios.get(`${this.$apiAdress}/api/incentive-attributions/summary`, {
        params: this.queryParams()
      })
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.summary = { ...DEFAULT_SUMMARY, ...data }
        })
        .catch((error) => {
          console.log(error)
          this.summary = { ...DEFAULT_SUMMARY }
        })
    },
    loadPreview (page) {
      this.isLoading = true
      const targetPage = Number(page) || Number(this.pagination.current_page) || 1
      const params = Object.assign({}, this.queryParams(), {
        page: targetPage,
        per_page: this.pagination.per_page || 25
      })

      axios.get(`${this.$apiAdress}/api/incentive-attributions/preview`, { params })
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.attributions = Array.isArray(data.data) ? data.data : []
          this.pagination = {
            current_page: Number(data.current_page) || 1,
            per_page: Number(data.per_page) || 25,
            total: Number(data.total) || 0,
            last_page: Number(data.last_page) || 1
          }
        })
        .catch((error) => {
          console.log(error)
          this.attributions = []
          this.errorMessage = this.parseError(error, 'Unable to load attribution preview.')
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    applyFilters () {
      this.refresh()
    },
    onSearchInput (value) {
      this.filters.search = value
      // Debounce so the summary + preview only refetch once the admin
      // pauses typing — search runs server-side across all pages.
      if (this.searchDebounce) clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.refresh()
      }, 400)
    },
    resetFilters () {
      if (this.searchDebounce) clearTimeout(this.searchDebounce)
      this.filters = {
        from: startOfMonthIso(),
        to: todayIso(),
        agent_id: '',
        status: '',
        search: ''
      }
      this.refresh()
    },
    goToPage (page) {
      if (Number(page) === Number(this.pagination.current_page)) return
      this.loadPreview(Number(page))
    },
    exportXlsx () {
      // Fetch as a blob so the download honours the active filters
      // and the Sanctum token; build a Blob URL and click a synthetic
      // <a> to trigger the save dialog. window.open with a query
      // token would also work, but goes through the browser's URL
      // bar and exposes the token in history.
      this.isExporting = true
      this.errorMessage = ''

      axios.get(`${this.$apiAdress}/api/incentive-attributions/export`, {
        params: this.queryParams(),
        responseType: 'blob'
      })
        .then((response) => {
          const blob = new Blob(
            [response.data],
            { type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
          )
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          const stamp = new Date().toISOString().slice(0, 10)
          link.href = url
          link.download = `incentive_preview_${stamp}.xlsx`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Export failed.')
        })
        .finally(() => {
          this.isExporting = false
        })
    },
    creditOne (id) {
      if (!id || this.creditingIds[id]) return
      this.$set(this.creditingIds, id, true)
      this.errorMessage = ''
      this.successMessage = ''

      axios.post(`${this.$apiAdress}/api/incentive-attributions/${id}/credit?token=${this.authToken()}`)
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.successMessage = data.message || 'Credited to agent account.'
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Credit failed.')
        })
        .finally(() => {
          this.$set(this.creditingIds, id, false)
        })
    },
    creditAll () {
      if (this.isCreditingAll) return
      if (!this.summary.eligible) return
      this.confirmCreditAllOpen = true
    },
    confirmCreditAll () {
      this.isCreditingAll = true
      this.errorMessage = ''
      this.successMessage = ''

      axios.post(
        `${this.$apiAdress}/api/incentive-attributions/credit-all?token=${this.authToken()}`,
        this.queryParams()
      )
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.successMessage = data.message || 'Credits applied.'
          this.confirmCreditAllOpen = false
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Bulk credit failed.')
          this.confirmCreditAllOpen = false
        })
        .finally(() => {
          this.isCreditingAll = false
        })
    },
    cancelCreditAll () {
      if (this.isCreditingAll) return
      this.confirmCreditAllOpen = false
    },
    recompute () {
      this.isRecomputing = true
      this.errorMessage = ''
      this.successMessage = ''

      axios.post(`${this.$apiAdress}/api/incentive-attributions/recompute?token=${this.authToken()}`)
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.successMessage = data.message || 'Recompute complete.'
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.errorMessage = this.parseError(error, 'Recompute failed.')
        })
        .finally(() => {
          this.isRecomputing = false
        })
    },
    statusLabel (status) {
      switch (status) {
        case 'eligible':              return 'Eligible'
        case 'ineligible_no_call':    return 'No Call'
        case 'ineligible_oms':        return 'OMS Customer'
        case 'ineligible_returning':  return 'Return Visit'
        case 'needs_review':          return 'Needs Review'
        case 'needs_review_oms':      return 'OMS Shared'
        case 'locked':                return 'Locked'
        default:                      return status || '—'
      }
    },
    statusPillClass (status) {
      switch (status) {
        case 'eligible':              return 'ip-pill--green'
        case 'locked':                return 'ip-pill--blue'
        case 'needs_review':          return 'ip-pill--red'
        case 'needs_review_oms':      return 'ip-pill--violet'
        case 'ineligible_no_call':    return 'ip-pill--gray'
        case 'ineligible_oms':        return 'ip-pill--amber'
        case 'ineligible_returning':  return 'ip-pill--amber'
        default:                      return 'ip-pill--gray'
      }
    },
    matchTypeLabel (type) {
      switch (type) {
        case 'mobile':    return 'Mobile'
        case 'name':      return 'Name'
        case 'ambiguous': return 'Ambiguous'
        default:          return ''
      }
    },
    reviewVariantClass (row) {
      // Colour the Review button by the row's verdict so admins can scan
      // the action column at a glance.
      const status = row._raw_status
      if (status === 'needs_review') {
        return row._match_type === 'ambiguous' ? 'ip-credit-btn--review' : 'ip-credit-btn--approve'
      }
      if (status === 'ineligible_no_call') return 'ip-credit-btn--muted'
      // ineligible_oms / ineligible_returning
      return 'ip-credit-btn--review'
    },
    reviewTitle (row) {
      switch (row._raw_status) {
        case 'needs_review':
          return row._match_type === 'ambiguous'
            ? 'Multiple agents called this name + place — review and pick the winner'
            : 'Review this name + place match before approving the ₹50 credit'
        case 'ineligible_no_call':
          return 'No call auto-matched — review and assign the agent who raised this patient as theirs'
        case 'ineligible_oms':
          return 'Flagged as an existing OMS customer — review before overriding and crediting'
        case 'ineligible_returning':
          return 'Flagged as a return visit — review before overriding and crediting'
        default:
          return 'Review and assign this attribution'
      }
    },
    formatAmount (v) {
      const n = Number(v) || 0
      return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },
    formatDate (value) {
      return formatDate(value)
    },
    formatDateTime (value) {
      return formatDateTime(value)
    },
    // Order Date for an OMS order row. Prefer `order_date` (a real datetime).
    // `created_at` is cast to `timestamp` on the Order model, so it serialises
    // as Unix *seconds* — passing that to `new Date()` reads it as ms and
    // renders 1970, so convert to ms before formatting.
    orderDateDisplay (order) {
      if (!order) return '—'
      if (order.order_date) return formatDate(order.order_date)
      const ts = Number(order.created_at)
      if (Number.isFinite(ts) && ts > 0) return formatDate(ts * 1000)
      return formatDate(order.created_at)
    },
    // Call.created_at is cast to `timestamp` on the Call model, so it serialises
    // as Unix *seconds* — passing that to `new Date()` reads it as ms and renders
    // 1970. Convert seconds → ms before formatting.
    callDateTimeDisplay (value) {
      const ts = Number(value)
      if (Number.isFinite(ts) && ts > 0) return formatDateTime(ts * 1000)
      return formatDateTime(value)
    },
    openReview (row) {
      this.reviewMode = 'review'
      this.reviewRow = row
      this.reviewPatient = {}
      this.reviewCandidates = []
      this.reviewPlaceTokens = []
      this.reviewError = ''
      this.pickedCallId = null
      this.pickedAgentId = null
      this.manualAgentId = ''
      this.reviewNote = ''
      this.reviewOpen = true
      this.loadCandidates(row._id)
    },
    /**
     * Eligible rows only — the engine matched by mobile and stamped an
     * agent, but the admin knows it credited the wrong person. Reuses the
     * review modal (same `/credit` endpoint, same footer) with the agent
     * dropdown pre-filled to whoever it currently credits, so approving
     * unchanged behaves exactly like the plain Credit button.
     */
    openEdit (row) {
      this.reviewMode = 'edit'
      this.reviewRow = row
      // No /candidates call: that endpoint 422s on eligible rows
      // ("No candidate list for this attribution type") and its name+place
      // search is meaningless for a mobile match anyway. The patient fields
      // the edit modal shows are already in the preview payload.
      const raw = this.attributions.find((a) => a.id === row._id)
      this.reviewPatient = raw && raw.patient ? raw.patient : {}
      this.reviewCandidates = []
      this.reviewPlaceTokens = []
      this.reviewError = ''
      this.pickedCallId = null
      this.pickedAgentId = null
      this.manualAgentId = row._agent_user_id ? String(row._agent_user_id) : ''
      this.reviewNote = ''
      this.reviewOpen = true
    },
    closeReview () {
      if (this.isApprovingReview) return
      this.reviewOpen = false
      this.reviewMode = 'review'
      this.reviewRow = null
      this.reviewCandidates = []
      this.reviewPatient = {}
      this.reviewPlaceTokens = []
      this.reviewError = ''
      this.pickedCallId = null
      this.pickedAgentId = null
      this.manualAgentId = ''
      this.reviewNote = ''
    },
    loadCandidates (id) {
      if (!id) return
      this.isLoadingCandidates = true
      axios.get(`${this.$apiAdress}/api/incentive-attributions/${id}/candidates`, {
        params: { token: this.authToken() }
      })
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.reviewPatient = data.patient || {}
          this.reviewCandidates = Array.isArray(data.candidates) ? data.candidates : []
          this.reviewPlaceTokens = Array.isArray(data.place_tokens) ? data.place_tokens : []
          this.reviewWindowDays = Number(data.window_days) || 60

          // Pre-select for name matches (single agent — admin just
          // needs to verify). For ambiguous rows, leave unselected so
          // admin must consciously pick.
          if (this.reviewRow && this.reviewRow._match_type === 'name' && this.reviewCandidates.length) {
            const preferred = this.reviewCandidates[0]
            this.pickedCallId = preferred.call_id
            this.pickedAgentId = preferred.agent_user_id
          }
        })
        .catch((error) => {
          console.log(error)
          this.reviewError = this.parseError(error, 'Unable to load candidates.')
        })
        .finally(() => {
          this.isLoadingCandidates = false
        })
    },
    pickCandidate (cand) {
      this.pickedCallId = cand.call_id
      this.pickedAgentId = cand.agent_user_id
      // Picking a candidate call and a manual agent are mutually
      // exclusive — clear the dropdown when a call is chosen.
      this.manualAgentId = ''
    },
    approveReview () {
      // The agent comes from a picked candidate call or, when no call
      // matched, the manual dropdown. One of the two must be set.
      const agentId = this.pickedAgentId || (this.manualAgentId ? Number(this.manualAgentId) : null)
      if (!this.reviewRow || !agentId) return
      this.isApprovingReview = true
      this.reviewError = ''

      const payload = { agent_user_id: agentId }
      if (this.pickedCallId) payload.call_id = this.pickedCallId
      if (this.reviewNote && this.reviewNote.trim()) payload.note = this.reviewNote.trim()

      axios.post(
        `${this.$apiAdress}/api/incentive-attributions/${this.reviewRow._id}/credit?token=${this.authToken()}`,
        payload
      )
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.successMessage = data.message || 'Credited to agent account.'
          this.reviewOpen = false
          this.reviewMode = 'review'
          this.reviewRow = null
          this.reviewCandidates = []
          this.pickedCallId = null
          this.pickedAgentId = null
          this.manualAgentId = ''
          this.reviewNote = ''
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.reviewError = this.parseError(error, 'Approval failed.')
        })
        .finally(() => {
          this.isApprovingReview = false
        })
    },
    normalizeName (value) {
      return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '')
    },
    isDifferentPerson (omsContactName) {
      // True when the OMS order's customer name doesn't normalise to
      // the patient's name — the strongest hint the order belongs to a
      // different member of this shared household.
      const patient = this.normalizeName(this.omsReviewPatient.patient_name)
      const oms = this.normalizeName(omsContactName)
      if (!patient || !oms) return false
      return patient !== oms
    },
    openOmsReview (row) {
      this.omsReviewRow = row
      this.omsReviewPatient = {}
      this.omsReviewAgent = null
      this.omsReviewCall = null
      this.omsReviewMatchType = row._match_type || ''
      this.omsRegistrants = []
      this.omsOrders = []
      this.omsReviewError = ''
      this.omsReviewOpen = true
      this.loadOmsShared(row._id)
    },
    closeOmsReview () {
      if (this.isApprovingOms) return
      this.omsReviewOpen = false
      this.omsReviewRow = null
      this.omsReviewPatient = {}
      this.omsReviewAgent = null
      this.omsReviewCall = null
      this.omsReviewMatchType = ''
      this.omsRegistrants = []
      this.omsOrders = []
      this.omsReviewError = ''
    },
    loadOmsShared (id) {
      if (!id) return
      this.isLoadingOmsReview = true
      axios.get(`${this.$apiAdress}/api/incentive-attributions/${id}/oms-shared`, {
        params: { token: this.authToken() }
      })
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.omsReviewPatient = data.patient || {}
          this.omsReviewAgent = data.agent || null
          this.omsReviewCall = data.call || null
          this.omsReviewMatchType = data.match_type || this.omsReviewMatchType
          this.omsRegistrants = Array.isArray(data.registrants) ? data.registrants : []
          this.omsOrders = Array.isArray(data.oms_orders) ? data.oms_orders : []
        })
        .catch((error) => {
          console.log(error)
          this.omsReviewError = this.parseError(error, 'Unable to load OMS-shared details.')
        })
        .finally(() => {
          this.isLoadingOmsReview = false
        })
    },
    approveOmsReview () {
      if (!this.omsReviewRow || !this.omsReviewAgent) return
      this.isApprovingOms = true
      this.omsReviewError = ''

      axios.post(`${this.$apiAdress}/api/incentive-attributions/${this.omsReviewRow._id}/credit?token=${this.authToken()}`)
        .then((response) => {
          const data = response && response.data ? response.data : {}
          this.successMessage = data.message || 'Credited to agent account.'
          this.omsReviewOpen = false
          this.omsReviewRow = null
          this.refresh()
        })
        .catch((error) => {
          console.log(error)
          this.omsReviewError = this.parseError(error, 'Credit failed.')
        })
        .finally(() => {
          this.isApprovingOms = false
        })
    }
  }
}
</script>

<style scoped>
.ip-page { padding: 0.25rem 0; }

.ip-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.ip-toolbar__title { display: flex; align-items: center; gap: 0.65rem; }
.ip-toolbar__title h1 { margin: 0; font-size: 1.15rem; font-weight: 700; color: #1f2a44; letter-spacing: -0.01em; }
.ip-toolbar__title p  { margin: 0.1rem 0 0; font-size: 0.78rem; color: #6b7280; }
.ip-toolbar__accent { width: 4px; height: 32px; border-radius: 4px; background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%); }

.ip-toolbar__actions { display: inline-flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }

.ip-recompute-btn,
.ip-export-btn {
  display: inline-flex; align-items: center; gap: 0.4rem;
  height: 34px; padding: 0 0.9rem; border-radius: 8px;
  background: #ffffff; border: 1px solid #e5e9f2; color: #1f2a44;
  font-weight: 600; font-size: 0.82rem; cursor: pointer;
}
.ip-recompute-btn:hover:not(:disabled),
.ip-export-btn:hover:not(:disabled) { border-color: #16a07a; color: #0e5b46; background: #f5fbf8; }
.ip-recompute-btn:disabled,
.ip-export-btn:disabled { opacity: 0.7; cursor: progress; }
.ip-recompute-btn svg.is-spinning { animation: ip-spin 0.9s linear infinite; }

.ip-credit-all-btn {
  display: inline-flex; align-items: center; gap: 0.4rem;
  height: 34px; padding: 0 0.9rem; border-radius: 8px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0; color: #fff; font-weight: 600; font-size: 0.82rem;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  cursor: pointer;
}
.ip-credit-all-btn:hover:not(:disabled) {
  transform: translateY(-1px); box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28); filter: brightness(1.05);
}
.ip-credit-all-btn:disabled { opacity: 0.55; cursor: not-allowed; }
@keyframes ip-spin { to { transform: rotate(360deg); } }

.ip-alert { margin: 0 0 0.75rem; border-radius: 10px; }

.ip-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.85rem;
  margin-bottom: 1rem;
}

.ip-tile {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  padding: 0.9rem 1rem;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.ip-tile__label { font-size: 0.74rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
.ip-tile__value { font-size: 1.5rem; font-weight: 700; color: #1f2a44; }
.ip-tile__breakdown { display: flex; flex-direction: column; gap: 0.1rem; font-size: 0.72rem; color: #64748b; }

.ip-tile--eligible .ip-tile__value { color: #166534; }
.ip-tile--ineligible .ip-tile__value { color: #92400e; }
.ip-tile--review .ip-tile__value { color: #b91c1c; }
.ip-tile--payout .ip-tile__value { color: #0e5b46; }

.ip-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.ip-section__head {
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.ip-section__head h2 {
  margin: 0; font-size: 0.95rem; font-weight: 700; color: #1f2a44;
}

.ip-section__body { padding: 1rem; }

.ip-label { display: block; margin-bottom: 0.3rem; font-size: 0.78rem; font-weight: 600; color: #4f5560; }

.ip-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.ip-field { display: flex; flex-direction: column; flex: 1 1 200px; min-width: 0; }
.ip-field--action { flex: 0 0 auto; min-width: 110px; }
.ip-field--full { flex: 1 1 100%; }

.ip-apply-btn {
  height: 36px; padding: 0 1.1rem;
  border-radius: 8px; border: 0;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #fff; font-weight: 600; font-size: 0.85rem;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  cursor: pointer;
}

.ip-apply-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28); filter: brightness(1.05); }
.ip-apply-btn:disabled { opacity: 0.55; cursor: progress; }

.ip-reset-btn {
  height: 36px; padding: 0 1rem;
  border-radius: 8px;
  background: #ffffff; border: 1px solid #d8dde6;
  color: #1f2a44; font-weight: 600; font-size: 0.85rem; cursor: pointer;
}
.ip-reset-btn:hover { border-color: #94a3b8; }

.ip-count { font-size: 0.78rem; color: #6b7280; }

.ip-reason { font-size: 0.82rem; color: #64748b; }
.ip-amount { color: #64748b; }
.ip-amount--paid { color: #166534; font-weight: 700; }

.ip-credit-btn {
  height: 28px; padding: 0 0.7rem;
  min-width: 78px; /* same box as "Review" / "✓ Credited" so they align */
  border: 0; border-radius: 6px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #fff; font-size: 0.75rem; font-weight: 700;
  cursor: pointer; letter-spacing: 0.02em;
  box-shadow: 0 1px 3px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}
.ip-credit-btn:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.05); box-shadow: 0 4px 10px rgba(14, 91, 70, 0.28); }
.ip-credit-btn:disabled { opacity: 0.55; cursor: progress; }

.ip-credit-btn--approve {
  background: linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%);
  box-shadow: 0 1px 3px rgba(29, 78, 216, 0.22);
}
.ip-credit-btn--approve:hover:not(:disabled) {
  box-shadow: 0 4px 10px rgba(29, 78, 216, 0.32);
}

/* Right-aligned so the primary action lines up in a single vertical column
   across every row — "+ Credit", "Review" and "✓ Credited" share the same
   min-width below, so Edit is the only thing that extends leftward. */
.ip-row-actions {
  display: inline-flex;
  gap: 0.4rem;
  align-items: center;
  justify-content: flex-end;
}

/* Secondary to "+ Credit", sits to its left — outlined so the gradient stays
   the primary action; green hover per the row-action convention. */
.ip-edit-btn {
  height: 28px; padding: 0 0.7rem;
  border: 1px solid #e5e9f2; border-radius: 6px;
  background: #fff;
  color: #4f5560; font-size: 0.75rem; font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}
.ip-edit-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}
.ip-edit-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.ip-edit-btn:focus { outline: none; }
.ip-edit-btn::-moz-focus-inner { border: 0; }
.ip-edit-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.ip-current-agent {
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  background: #fff;
  padding: 0.75rem 0.9rem;
}
.ip-current-agent__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.3rem;
}
.ip-current-agent__name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
}
.ip-current-agent__hint {
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 1.45;
}

.ip-credited {
  display: inline-block;
  min-width: 78px; text-align: center;
  font-size: 0.72rem; font-weight: 700; letter-spacing: 0.02em;
  color: #1d4ed8;
}
.ip-no-action { color: #94a3b8; }

.ip-pill {
  display: inline-block;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.ip-pill--green { background: #dcfce7; color: #166534; }
.ip-pill--blue  { background: #dbeafe; color: #1d4ed8; }
.ip-pill--amber { background: #fef3c7; color: #92400e; }
.ip-pill--red   { background: #fee2e2; color: #991b1b; }
.ip-pill--gray  { background: #e5e7eb; color: #374151; }
.ip-pill--violet { background: #ede9fe; color: #6d28d9; }

.ip-agent-cell { display: flex; flex-direction: column; gap: 0.2rem; align-items: flex-start; }
.ip-agent-name { color: #1f2a44; font-weight: 500; }

.ip-mobile-cell { display: flex; flex-direction: column; gap: 0.15rem; }
.ip-mobile-primary { color: #1f2a44; }
.ip-mobile-oms { font-size: 0.72rem; color: #1d4ed8; font-weight: 600; letter-spacing: 0.01em; }
.ip-match-pill {
  display: inline-block;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.ip-match-pill--name { background: #dbeafe; color: #1d4ed8; }
.ip-match-pill--ambiguous { background: #fee2e2; color: #991b1b; }
.ip-match-pill--mobile { background: #dcfce7; color: #166534; }

.ip-pagination-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.75rem;
}

.ip-pagination-info { font-size: 0.8rem; color: #6b7280; }

.ip-credit-btn--review {
  background: linear-gradient(135deg, #b45309 0%, #f59e0b 100%);
  box-shadow: 0 1px 3px rgba(180, 83, 9, 0.22);
}
.ip-credit-btn--review:hover:not(:disabled) {
  box-shadow: 0 4px 10px rgba(180, 83, 9, 0.30);
}

.ip-credit-btn--oms {
  background: linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%);
  box-shadow: 0 1px 3px rgba(109, 40, 217, 0.22);
}
.ip-credit-btn--oms:hover:not(:disabled) {
  box-shadow: 0 4px 10px rgba(109, 40, 217, 0.30);
}

.ip-credit-btn--muted {
  background: linear-gradient(135deg, #475569 0%, #64748b 100%);
  box-shadow: 0 1px 3px rgba(71, 85, 105, 0.22);
}
.ip-credit-btn--muted:hover:not(:disabled) {
  box-shadow: 0 4px 10px rgba(71, 85, 105, 0.30);
}

.ip-manual-assign {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  padding: 0.75rem 0.9rem;
}
.ip-manual-note {
  width: 100%;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  padding: 0.5rem 0.65rem;
  font-size: 0.85rem;
  color: #1f2a44;
  resize: vertical;
  font-family: inherit;
}
.ip-manual-note:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.ip-review-modal >>> .modal-content {
  border-radius: 14px;
  border: 1px solid #e5e9f2;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
  overflow: hidden;
}
.ip-review-modal >>> .modal-header {
  background: #fff;
  border-bottom: 1px solid #eef1f7;
  padding: 0.95rem 1.15rem;
}
.ip-review-modal >>> .modal-header .modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1f2a44;
}
.ip-review-modal >>> .modal-body {
  background: #fbfcfe;
  padding: 1.1rem;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}
.ip-review-modal >>> .modal-footer {
  background: #fff;
  border-top: 1px solid #eef1f7;
  padding: 0.7rem 1.15rem;
  gap: 0.5rem;
}

.ip-review-body { display: flex; flex-direction: column; gap: 0.9rem; }

.ip-review-alert {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 0.55rem 0.85rem;
  font-size: 0.82rem;
}

.ip-review-patient {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  overflow: hidden;
}
.ip-review-patient__head {
  padding: 0.7rem 0.9rem;
  background: #f5fbf8;
  border-bottom: 1px solid #eef1f7;
}
.ip-review-eyebrow {
  display: block;
  font-size: 0.68rem;
  font-weight: 700;
  color: #16a07a;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.ip-review-patient__head h3 {
  margin: 0.15rem 0 0;
  font-size: 1rem;
  font-weight: 700;
  color: #1f2a44;
}
.ip-review-patient__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.45rem 1rem;
  padding: 0.7rem 0.9rem;
}
.ip-review-patient__grid > div { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.ip-review-patient__place { grid-column: 1 / -1; }

.ip-rk { font-size: 0.7rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
.ip-rv { font-size: 0.85rem; color: #1f2a44; }

.ip-token {
  display: inline-block;
  padding: 0.08rem 0.4rem;
  margin: 0.1rem 0.2rem 0 0;
  border-radius: 999px;
  background: #e5e7eb;
  color: #374151;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}
.ip-token--match { background: #dcfce7; color: #166534; }

.ip-review-head h4 {
  margin: 0 0 0.2rem;
  font-size: 0.92rem;
  font-weight: 700;
  color: #1f2a44;
}
.ip-review-head p {
  margin: 0;
  font-size: 0.78rem;
  color: #64748b;
}

.ip-review-empty {
  background: #ffffff;
  border: 1px dashed #d8dde6;
  border-radius: 10px;
  padding: 1.1rem;
  text-align: center;
  font-size: 0.85rem;
  color: #64748b;
}

.ip-review-cards { display: flex; flex-direction: column; gap: 0.6rem; }

.ip-cand {
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  padding: 0.75rem 0.9rem;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}
.ip-cand:hover { border-color: #16a07a; background: #f8fdfb; }
.ip-cand.is-picked {
  border-color: #16a07a;
  background: #f5fbf8;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.ip-cand__radio {
  margin: 0.25rem 0 0;
  accent-color: #16a07a;
  flex: 0 0 auto;
}

.ip-cand__body { display: flex; flex-direction: column; gap: 0.3rem; min-width: 0; flex: 1 1 auto; }

.ip-cand__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.ip-cand__agent { font-size: 0.95rem; font-weight: 700; color: #0e5b46; }
.ip-cand__when { font-size: 0.75rem; color: #64748b; }

.ip-cand__row {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 0.5rem;
  align-items: baseline;
}
.ip-cand__row .ip-rv { font-size: 0.82rem; }

.ip-cand__status { text-transform: uppercase; font-weight: 600; font-size: 0.72rem; color: #1d4ed8; letter-spacing: 0.03em; }
.ip-cand__addr { display: block; }
.ip-cand__notes { white-space: pre-wrap; color: #475569; }

.ip-oms-note {
  margin: 0;
  padding: 0.6rem 0.85rem;
  border-radius: 8px;
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  color: #5b21b6;
  font-size: 0.8rem;
  line-height: 1.4;
}

.ip-oms-agent {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
}
.ip-oms-agent__name { font-size: 0.95rem; font-weight: 700; color: #0e5b46; }
.ip-oms-agent__call { font-size: 0.75rem; color: #64748b; }

.ip-oms-list { display: flex; flex-direction: column; gap: 0.5rem; }

.ip-oms-item {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  padding: 0.65rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.ip-oms-item__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.ip-oms-item__title { font-size: 0.88rem; font-weight: 700; color: #1f2a44; }
.ip-oms-item__amount { font-size: 0.85rem; font-weight: 700; color: #166534; }
.ip-oms-item__when { font-size: 0.75rem; color: #64748b; }
.ip-oms-item__row {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 0.5rem;
  align-items: baseline;
}
.ip-oms-item__row .ip-rv { font-size: 0.82rem; }

.ip-oms-diff { color: #6d28d9; font-weight: 600; }
.ip-oms-tag {
  display: inline-block;
  margin-left: 0.4rem;
  padding: 0.06rem 0.4rem;
  border-radius: 999px;
  background: #ede9fe;
  color: #6d28d9;
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.ip-modal-btn {
  height: 34px;
  min-width: 90px;
  padding: 0.45rem 1.1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 0;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}
.ip-modal-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #fff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}
.ip-modal-btn--primary:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28); filter: brightness(1.05); }
.ip-modal-btn--ghost {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}
.ip-modal-btn--ghost:hover:not(:disabled) { background: #f8fafc; }
.ip-modal-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.ip-modal-btn:focus { outline: none; }
.ip-modal-btn::-moz-focus-inner { border: 0; }
.ip-modal-btn--primary:focus-visible { outline: none; box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18), 0 0 0 3px rgba(22, 160, 122, 0.22); }
.ip-modal-btn--ghost:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22); }
</style>
